/* eslint-disable no-param-reassign */

import RatingMenu from '@app/common/Algolia/Widgets/RatingMenu';
import Box from '@cavilha/box';
import Collapse, { CollapseItem } from '@cavilha/collapse';
import {
  applyVisualFilter,
  facetsNames,
  getItemFacetCount,
  getRemoveFacetsList,
  parseFacetName,
} from '@helpers/filters/filters';
import { RemoveFromAutomaticFilters } from 'helpers/algolia/constants';
import isMobile from 'helpers/isMobile';
import { getValuesRuleContextFacets } from 'hooks/algolia/useListFilter';
import { useState } from 'react';
import { connectStateResults } from 'react-instantsearch-dom';
import {
  HierarchicalMenuDesktop,
  HierarchicalMenuMobile,
} from '../HierarchicalMenu/HierarchicalMenu';
import RangeInput from '../RangeInput/RangeInput';
import RefinementList from '../RefinementList';
import ToggleRefinementList from '../ToggleRefinementList';
import VisualFilters from '../VisualFilters';

type RuleContextCollapsesProps = {
  countFacets: number;
  appliedFacets: Array<string>;
  facetsElement: Array<any>;
};

const DEFAULT_AUTOMATIC_FILTERS_COUNT = 20;

export const redirectPage = (searchResults: Record<string, any>): string => {
  if (searchResults) {
    const { userData = [] } = searchResults;

    const [redirectRule] = userData.filter(data => data?.redirect);

    if (redirectRule) {
      return redirectRule.redirect;
    }
  }

  return null;
};

const checkCollapseHaveToggles = (toggles, itemFacetToggleCount) => {
  const facetToggleNames = Object.keys(itemFacetToggleCount);

  return toggles.some(toggle => facetToggleNames.includes(toggle.attribute));
};

const shouldIncludeAutomaticFilter = (
  itemName: string,
  excludedAttributes: string[] = []
): boolean =>
  !excludedAttributes.includes(itemName) &&
  !RemoveFromAutomaticFilters.includes(itemName) &&
  itemName.indexOf('flags.') !== 0 &&
  itemName.indexOf('extra.merchandising.') !== 0 &&
  itemName.indexOf('sellers_flags.') !== 0;

const addAutomaticFacetToResult = ({
  facetNameToShow,
  visualFilter,
  accumulator,
  isCollapseOpen,
}: {
  facetNameToShow: string;
  visualFilter: any;
  accumulator: RuleContextCollapsesProps;
  isCollapseOpen: string[];
}) => {
  accumulator.countFacets++;
  accumulator.appliedFacets.push(facetNameToShow);
  const facetLabelToShow = parseFacetName(facetNameToShow);
  const collapseValueToShow = facetsNames({
    widgetName: 'RefinementList',
    attribute: facetNameToShow,
  });

  let refinementComponent = (
    <RefinementList key={facetNameToShow} attribute={facetNameToShow} limit={10} showMore />
  );

  if (visualFilter) {
    refinementComponent = (
      <VisualFilters
        key={facetNameToShow}
        attribute={facetNameToShow}
        limit={6}
        filters={visualFilter}
        showMore
      />
    );
  }

  accumulator.facetsElement.push(
    <CollapseItem
      key={collapseValueToShow}
      value={collapseValueToShow}
      title={facetLabelToShow}
      isOpen={isCollapseOpen.includes(collapseValueToShow)}
      css={{ background: '$neutral1' }}
      titleColor="$neutral6"
      headerBackgroundColor="$neutral1"
      forceMount
    >
      {refinementComponent}
    </CollapseItem>
  );
};

const RuleContextFacets = connectStateResults(
  ({ searchResults, device, inputs, algoliaFilters = [], visualFilters }) => {
    const deviceToRender = isMobile() ? 'mobile' : 'desktop';

    if (device !== deviceToRender) {
      return null;
    }

    let facets = [];

    if (searchResults && searchResults.nbHits) {
      const { disjunctiveFacets: algoliaFacets } = searchResults;
      const algoliaFacetsName = algoliaFacets.map(({ name }) => name);
      const flatFacetsData = { facets: algoliaFilters };
      const isRedirectSeller = redirectPage(searchResults);
      if (isRedirectSeller) {
        window.location.href = isRedirectSeller;
        return null;
      }

      if (flatFacetsData?.facets.length === 0) {
        return <Box css={{ height: '80vh', bc: '$neutral1' }} />;
      }
      let hasHierarchicalMenu = false;
      const ruleContextFacets = flatFacetsData.facets
        .map(facet => {
          if (facet.widgetName === 'RemoveFacets') {
            return null;
          }
          if (facet.widgetName === 'HierarchicalMenu') {
            if (hasHierarchicalMenu) {
              return null;
            }
            hasHierarchicalMenu = true;
          }
          return facet;
        })
        .filter(facet => facet != null)
        .sort((a, b) => a.priority - b.priority)
        .map(item => applyVisualFilter(item, visualFilters));

      const removeFacetsList = getRemoveFacetsList(flatFacetsData);

      const itemFacetCount = getItemFacetCount(algoliaFacets, removeFacetsList);

      const itemFacetToggleCount = algoliaFacets
        .filter(
          facet =>
            facet.name.startsWith('sellers_flags.') ||
            (facet.name.startsWith('extra.') && !removeFacetsList.includes(facet.name))
        )
        .reduce((prevVal: any, item: any) => {
          const { name, stats, data: { true: dataTrue = 0 } = {} } = item;
          if (stats !== undefined || !dataTrue) {
            return prevVal;
          }
          return {
            ...prevVal,
            [name]: dataTrue,
          };
        }, {});

      itemFacetCount.sort((a: any, b: any) => b.count - a.count);

      facets = itemFacetCount.map(facetAttribute => {
        const visualFilter = visualFilters[facetAttribute.name] ?? null;

        return { name: facetAttribute.name, visualFilter };
      });

      const attributesToShow = ruleContextFacets
        .map(({ attribute }) => attribute)
        .filter(item => item);

      const collpaseItemsAutomaticToShow = facets
        .filter(({ name: item }) => shouldIncludeAutomaticFilter(item, attributesToShow))
        .map(({ name }) => facetsNames({ widgetName: 'RefinementList', attribute: name }));

      const collapseOpenName =
        deviceToRender === 'desktop' ? 'collapseOpenDesktop' : 'collapseOpenMobile';

      const collapseItemsToShow = ruleContextFacets
        .filter(({ [collapseOpenName]: collapseOpen }) => collapseOpen)
        .map(facetsNames);
      const [isCollapseOpen, setIsCollapseOpen] = useState([
        ...collapseItemsToShow,
        ...collpaseItemsAutomaticToShow,
      ]);

      if (ruleContextFacets.length) {
        const hasAutomaticWidget = ruleContextFacets.some(
          facet => facet.widgetName === 'Automatic'
        );

        const result = Object.values(ruleContextFacets).reduce(
          (prevVal: RuleContextCollapsesProps, facet: Record<string, any>) => {
            const { attribute, widgetName } = facet;
            const facetName = attribute || widgetName;
            const collapseValue = facetsNames(facet);
            const facetLabel = parseFacetName(facetName);
            const algoliaFacetsShow = algoliaFacetsName.includes(facetName);
            const facetNameToAppliedFacets = Array.isArray(facetName)
              ? facetName.join('_')
              : facetName;
            const isOpen = isCollapseOpen.includes(collapseValue);
            if (!prevVal.appliedFacets.includes(facetNameToAppliedFacets)) {
              if (
                facet.widgetName === 'RangeInput' &&
                algoliaFacetsShow &&
                !removeFacetsList.includes(facetName)
              ) {
                prevVal.countFacets++;
                prevVal.appliedFacets.push(facetNameToAppliedFacets);

                prevVal.facetsElement.push(
                  <CollapseItem
                    key={collapseValue}
                    value={collapseValue}
                    title={facetLabel}
                    css={{ background: '$neutral1' }}
                    isOpen={isOpen}
                    titleColor="$neutral6"
                    headerBackgroundColor="$neutral1"
                    forceMount
                  >
                    <RangeInput
                      key={facetName}
                      unit={facet.attributeType}
                      attribute={facetName}
                      inputs={inputs}
                    />
                  </CollapseItem>
                );
              }
              if (facet.widgetName === 'HierarchicalMenu') {
                prevVal.countFacets++;
                prevVal.appliedFacets.push(facetNameToAppliedFacets);

                prevVal.facetsElement.push(
                  <CollapseItem
                    key={collapseValue}
                    value={collapseValue}
                    title="Categoria"
                    css={{ background: '$neutral1' }}
                    isOpen={isOpen}
                    titleColor="$neutral6"
                    headerBackgroundColor="$neutral1"
                    forceMount
                  >
                    {isMobile() && (
                      <HierarchicalMenuMobile
                        key={facetName}
                        attributes={facetName}
                        showMore
                        limit={8}
                        showMoreLimit={24}
                      />
                    )}
                    {!isMobile && (
                      <HierarchicalMenuDesktop
                        key={facetName}
                        attributes={facetName}
                        showMore
                        limit={8}
                        showMoreLimit={24}
                      />
                    )}
                  </CollapseItem>
                );
              }
              if (facet.widgetName === 'RatingMenu' && algoliaFacetsShow) {
                prevVal.countFacets++;
                prevVal.appliedFacets.push(facetNameToAppliedFacets);

                prevVal.facetsElement.push(
                  <CollapseItem
                    key={collapseValue}
                    value={collapseValue}
                    title="Avaliações"
                    isOpen={isOpen}
                    css={{ background: '$neutral1' }}
                    titleColor="$neutral6"
                    headerBackgroundColor="$neutral1"
                    forceMount
                  >
                    <RatingMenu attribute="extra.reviews.rate" />
                  </CollapseItem>
                );
              }

              if (facet.widgetName === 'SectionToggle') {
                const { sectionLabel, toggle: toggles } = facet;
                const hasToogles = checkCollapseHaveToggles(toggles, itemFacetToggleCount);

                if (hasToogles) {
                  prevVal.countFacets++;
                  prevVal.facetsElement.push(
                    <CollapseItem
                      key={collapseValue}
                      value={collapseValue}
                      title={sectionLabel}
                      css={{ background: '$neutral1' }}
                      titleColor="$neutral6"
                      headerBackgroundColor="$neutral1"
                      isOpen={isOpen}
                      forceMount
                    >
                      {toggles.map((toggle: Record<string, any>, index) => (
                        <ToggleRefinementList
                          key={toggle.attribute}
                          attribute={toggle.attribute}
                          label={toggle.label}
                          type={sectionLabel}
                          position={index}
                          value
                        />
                      ))}
                    </CollapseItem>
                  );
                }
              }

              if (facet.widgetName === 'RefinementList' && algoliaFacetsShow) {
                prevVal.countFacets++;
                prevVal.appliedFacets.push(facetNameToAppliedFacets);

                let refinementComponent = (
                  <RefinementList key={facetName} attribute={facetName} showMore />
                );
                if (facet.visualFilter !== null) {
                  refinementComponent = (
                    <VisualFilters
                      key={facetName}
                      attribute={facetName}
                      showMore
                      filters={facet.visualFilter}
                      limit={6}
                    />
                  );
                }

                prevVal.facetsElement.push(
                  <CollapseItem
                    key={collapseValue}
                    value={collapseValue}
                    title={facetLabel}
                    css={{ background: '$neutral1' }}
                    titleColor="$neutral6"
                    headerBackgroundColor="$neutral1"
                    isOpen={isOpen}
                    forceMount
                  >
                    {refinementComponent}
                  </CollapseItem>
                );
              }

              if (facet.widgetName === 'Automatic') {
                const { count } = facet;
                const totalToShow = count - prevVal.countFacets;

                const facetsToShow = facets
                  .filter(({ name: item }) =>
                    shouldIncludeAutomaticFilter(item, prevVal.appliedFacets)
                  )
                  .slice(0, totalToShow);

                facetsToShow.forEach(({ name: facetNameToShow, visualFilter }) => {
                  addAutomaticFacetToResult({
                    facetNameToShow,
                    visualFilter,
                    accumulator: prevVal,
                    isCollapseOpen,
                  });
                });
              }
            }
            getValuesRuleContextFacets(prevVal.facetsElement);
            return prevVal;
          },
          {
            countFacets: 0,
            appliedFacets: [],
            facetsElement: [],
          }
        ) as RuleContextCollapsesProps;

        if (!hasAutomaticWidget) {
          const totalToShow = Math.max(0, DEFAULT_AUTOMATIC_FILTERS_COUNT - result.countFacets);

          if (totalToShow > 0) {
            const facetsToShow = facets
              .filter(({ name: item }) => shouldIncludeAutomaticFilter(item, result.appliedFacets))
              .slice(0, totalToShow);

            facetsToShow.forEach(({ name: facetNameToShow, visualFilter }) => {
              addAutomaticFacetToResult({
                facetNameToShow,
                visualFilter,
                accumulator: result,
                isCollapseOpen,
              });
            });
          }
        }

        const { facetsElement } = result;

        return (
          <Collapse
            columns={1}
            type="multiple"
            special={false}
            value={isCollapseOpen}
            onValueChange={value => {
              setIsCollapseOpen(value);
            }}
            css={{ mx: '$none' }}
          >
            {facetsElement}
          </Collapse>
        );
      }
    }
    return null;
  }
);

export default RuleContextFacets;
